import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import {
  NoteTokenType,
  type SpendingKeys,
  type JoinSplitNote,
} from "../joinsplit/types";
import { computeNullifier } from "../joinsplit/keys";
import { formatInboxPublicKeyHex } from "../utils/inbox-crypto";
import {
  buildRecoveryNotePayload,
  deriveRecoveryKeyFromMpkHex,
} from "../utils/recovery";
import { deriveBaseTokenHash } from "./base-token-hash";

export const FIELD_PRIME = BigInt(
  "21888242871839275222246405745257275088548364400416034343698204186575808495617",
);
export const ABI_CODER = ethers.AbiCoder.defaultAbiCoder();
export type EvmTokenType = "erc20" | "erc721" | "erc1155";
type RecoveryEnvelope = { recoveryKey: string; encryptedNote: string };
const EMPTY_RECOVERY_ENVELOPE: RecoveryEnvelope = {
  recoveryKey: ethers.ZeroHash,
  encryptedNote: "0x",
};

function evmNoteTokenType(tokenType: EvmTokenType): NoteTokenType {
  switch (tokenType) {
    case "erc721":
      return NoteTokenType.ERC721;
    case "erc1155":
      return NoteTokenType.ERC1155;
    case "erc20":
    default:
      return NoteTokenType.ERC20;
  }
}

function evmWithdrawTokenTypeCode(tokenType: EvmTokenType): number {
  switch (tokenType) {
    case "erc721":
      return 1;
    case "erc1155":
      return 2;
    case "erc20":
    default:
      return 0;
  }
}

export async function deriveTokenHash(
  tokenAddress: string,
  tokenType: EvmTokenType,
  tokenSubID?: string,
): Promise<bigint> {
  const normalizedTokenSubID = normalizeTokenSubID(tokenSubID);
  return deriveBaseTokenHash(
    tokenAddress,
    evmNoteTokenType(tokenType),
    normalizedTokenSubID,
  );
}

export function normalizeTokenSubID(tokenSubID?: string): string {
  return tokenSubID && tokenSubID.trim().length > 0 ? tokenSubID.trim() : "0";
}

function formatMpkHexForRecovery(mpk: bigint): string {
  return `0x${mpk.toString(16).padStart(64, "0")}`;
}

async function buildRecoveryEnvelopeForRecipient(
  note: JoinSplitNote,
  recipientMPK: bigint,
  recipientInboxPublicKeyHex?: string,
): Promise<RecoveryEnvelope> {
  if (!recipientInboxPublicKeyHex) {
    return EMPTY_RECOVERY_ENVELOPE;
  }
  return {
    recoveryKey: await deriveRecoveryKeyFromMpkHex(
      formatMpkHexForRecovery(recipientMPK),
      note.commitment,
    ),
    encryptedNote: await buildRecoveryNotePayload(
      note,
      recipientInboxPublicKeyHex,
    ),
  };
}

function hex32(value: string | bigint): string {
  return ethers.toBeHex(typeof value === "bigint" ? value : BigInt(value), 32);
}

export function computeWithdrawBoundHash(params: {
  chainId: bigint;
  contractAddress: string;
  merkleRoot: string;
  nullifiers: string[];
  recipient: string;
  tokenAddress: string;
  tokenHash: string;
  tokenType: EvmTokenType;
  tokenSubID?: string;
  amount: bigint;
  protocolFee?: bigint;
  partnerFee?: bigint;
  protocolFeeRecipient?: string;
  partnerFeeRecipient?: string;
}): string {
  if (EVM_CONFIG.WITHDRAW_BINDING_VERSION === "v2") {
    return ethers.toBeHex(
      BigInt(
        ethers.keccak256(
          ABI_CODER.encode(
            [
              "string",
              "uint256",
              "address",
              "address",
              "address",
              "bytes32",
              "uint8",
              "uint256",
              "uint256",
              "uint256",
              "uint256",
              "address",
              "address",
              "uint256",
            ],
            [
              "STEALTHOS_BASE_DECIDER_WITHDRAW_V2",
              params.chainId,
              ethers.getAddress(params.contractAddress),
              ethers.getAddress(params.recipient),
              ethers.getAddress(params.tokenAddress),
              params.tokenHash,
              evmWithdrawTokenTypeCode(params.tokenType),
              BigInt(normalizeTokenSubID(params.tokenSubID)),
              params.amount,
              params.protocolFee ?? 0n,
              params.partnerFee ?? 0n,
              ethers.getAddress(
                params.protocolFeeRecipient ?? ethers.ZeroAddress,
              ),
              ethers.getAddress(
                params.partnerFeeRecipient ?? ethers.ZeroAddress,
              ),
              params.nullifiers.length,
            ],
          ),
        ),
      ) % FIELD_PRIME,
      32,
    );
  }

  return ethers.toBeHex(
    BigInt(
      ethers.keccak256(
        ABI_CODER.encode(
          [
            "string",
            "uint256",
            "address",
            "address",
            "address",
            "bytes32",
            "uint8",
            "uint256",
            "uint256",
            "uint256",
          ],
          [
            "STEALTHOS_BASE_DECIDER_WITHDRAW_V1",
            params.chainId,
            ethers.getAddress(params.contractAddress),
            ethers.getAddress(params.recipient),
            ethers.getAddress(params.tokenAddress),
            params.tokenHash,
            evmWithdrawTokenTypeCode(params.tokenType),
            BigInt(normalizeTokenSubID(params.tokenSubID)),
            params.amount,
            params.nullifiers.length,
          ],
        ),
      ),
    ) % FIELD_PRIME,
    32,
  );
}

export function computeBatchWithdrawBoundHash(params: {
  chainId: bigint;
  contractAddress: string;
  merkleRoot: string;
  nullifiers: string[];
  commitments: string[];
  tokenAddress: string;
  tokenHash: string;
  outputs: Array<{ recipient: string; amount: bigint }>;
  totalAmount: bigint;
  protocolFee: bigint;
  partnerFee: bigint;
  protocolFeeRecipient: string;
  partnerFeeRecipient: string;
}): string {
  const normalizedOutputs = params.outputs.map((output) => ({
    recipient: ethers.getAddress(output.recipient),
    amount: output.amount,
  }));
  const outputsDigest = ethers.keccak256(
    ABI_CODER.encode(
      ["tuple(address recipient,uint256 amount)[]"],
      [normalizedOutputs],
    ),
  );

  if (EVM_CONFIG.WITHDRAW_BINDING_VERSION === "v2") {
    return ethers.toBeHex(
      BigInt(
        ethers.keccak256(
          ABI_CODER.encode(
            [
              "string",
              "uint256",
              "address",
              "address",
              "bytes32",
              "bytes32",
              "uint256",
              "uint256",
              "uint256",
              "address",
              "address",
              "uint256",
              "uint256",
              "uint256",
            ],
            [
              "STEALTHOS_BASE_DECIDER_BATCH_WITHDRAW_V2",
              params.chainId,
              ethers.getAddress(params.contractAddress),
              ethers.getAddress(params.tokenAddress),
              params.tokenHash,
              outputsDigest,
              params.totalAmount,
              params.protocolFee,
              params.partnerFee,
              ethers.getAddress(params.protocolFeeRecipient),
              ethers.getAddress(params.partnerFeeRecipient),
              params.nullifiers.length,
              params.commitments.length,
              normalizedOutputs.length,
            ],
          ),
        ),
      ) % FIELD_PRIME,
      32,
    );
  }

  return ethers.toBeHex(
    BigInt(
      ethers.keccak256(
        ABI_CODER.encode(
          [
            "string",
            "tuple(uint256 chainId,address pool,address token,bytes32 tokenHash,bytes32 outputsDigest,uint256 totalAmount,uint256 protocolFee,uint256 partnerFee,uint256 nullifierCount,uint256 commitmentCount,uint256 outputCount)",
          ],
          [
            "STEALTHOS_BASE_DECIDER_BATCH_WITHDRAW_V1",
            {
              chainId: params.chainId,
              pool: ethers.getAddress(params.contractAddress),
              token: ethers.getAddress(params.tokenAddress),
              tokenHash: params.tokenHash,
              outputsDigest,
              totalAmount: params.totalAmount,
              protocolFee: params.protocolFee,
              partnerFee: params.partnerFee,
              nullifierCount: params.nullifiers.length,
              commitmentCount: params.commitments.length,
              outputCount: normalizedOutputs.length,
            },
          ],
        ),
      ),
    ) % FIELD_PRIME,
    32,
  );
}

export function computePrivateSwapActionHash(params: {
  chainId: bigint;
  contractAddress: string;
  inputToken: string;
  inputTokenHash: string;
  inputAmount: bigint;
  outputToken: string;
  noteAmount: bigint;
  minOutputAmount: bigint;
  protocolFeeBps: bigint;
  partnerFeeBps: bigint;
  protocolFeeRecipient: string;
  partnerFeeRecipient: string;
  requiredGrossOutput: bigint;
  surplusRecipient: string;
  swapRouter: string;
  swapCallData: string;
  outputCommitment: string;
  outputTokenHash: string;
  outputRecoveryKey: string;
  outputEncryptedNote: string;
  nullifierCount: number;
  changeCommitment: string;
}): string {
  const digest = ethers.keccak256(
    ABI_CODER.encode(
      [
        "string",
        "uint256",
        "address",
        "address",
        "bytes32",
        "uint256",
        "address",
        "uint256",
        "uint256",
        "uint256",
        "uint256",
        "address",
        "address",
        "uint256",
        "address",
        "address",
        "bytes32",
        "bytes32",
        "bytes32",
        "bytes32",
        "bytes32",
        "uint256",
        "bytes32",
      ],
      [
        "STEALTHOS_BASE_DECIDER_PRIVATE_SWAP_V3",
        params.chainId,
        ethers.getAddress(params.contractAddress),
        ethers.getAddress(params.inputToken),
        params.inputTokenHash,
        params.inputAmount,
        ethers.getAddress(params.outputToken),
        params.noteAmount,
        params.minOutputAmount,
        params.protocolFeeBps,
        params.partnerFeeBps,
        ethers.getAddress(params.protocolFeeRecipient),
        ethers.getAddress(params.partnerFeeRecipient),
        params.requiredGrossOutput,
        ethers.getAddress(params.surplusRecipient),
        ethers.getAddress(params.swapRouter),
        ethers.keccak256(params.swapCallData),
        params.outputCommitment,
        params.outputTokenHash,
        params.outputRecoveryKey,
        ethers.keccak256(params.outputEncryptedNote),
        BigInt(params.nullifierCount),
        params.changeCommitment,
      ],
    ),
  );
  return ethers.toBeHex(BigInt(digest) % FIELD_PRIME, 32);
}

export function computePrivateBridgeActionHash(params: {
  chainId: bigint;
  contractAddress: string;
  inputToken: string;
  inputTokenHash: string;
  inputAmount: bigint;
  protocolFeeBps: number | bigint;
  partnerFeeBps: number | bigint;
  protocolFeeRecipient: string;
  partnerFeeRecipient: string;
  bridgedAmount: bigint;
  bridgeTarget: string;
  bridgeCallValue: bigint;
  bridgeCallData: string;
  expectedOrderId: string;
  bridgeOrderExpectationDigest: string;
  nullifierCount: number;
  changeCommitment: string;
}): string {
  const digest = ethers.keccak256(
    ABI_CODER.encode(
      [
        "string",
        "uint256",
        "address",
        "address",
        "bytes32",
        "uint256",
        "uint256",
        "uint256",
        "address",
        "address",
        "uint256",
        "address",
        "uint256",
        "bytes32",
        "bytes32",
        "bytes32",
        "uint256",
        "bytes32",
      ],
      [
        "STEALTHOS_BASE_DECIDER_PRIVATE_BRIDGE_V2",
        params.chainId,
        ethers.getAddress(params.contractAddress),
        ethers.getAddress(params.inputToken),
        params.inputTokenHash,
        params.inputAmount,
        params.protocolFeeBps,
        params.partnerFeeBps,
        ethers.getAddress(params.protocolFeeRecipient),
        ethers.getAddress(params.partnerFeeRecipient),
        params.bridgedAmount,
        ethers.getAddress(params.bridgeTarget),
        params.bridgeCallValue,
        params.expectedOrderId,
        ethers.keccak256(params.bridgeCallData),
        params.bridgeOrderExpectationDigest,
        BigInt(params.nullifierCount),
        params.changeCommitment,
      ],
    ),
  );
  return ethers.toBeHex(BigInt(digest) % FIELD_PRIME, 32);
}

interface BridgeOrderExpectation {
  takeChainId: bigint;
  takeAmount: bigint;
  takeTokenAddress: string;
  receiverDst: string;
  givePatchAuthoritySrc: string;
  orderAuthorityAddressDst: string;
  allowedTakerDst: string;
  allowedCancelBeneficiarySrc: string;
}

function normalizeBytes(
  value: unknown,
  field: string,
  { allowEmpty = false } = {},
): string {
  const raw = String(value || "").trim();
  const hex = raw.startsWith("0x") ? raw : `0x${raw}`;
  if (
    !/^0x[0-9a-fA-F]*$/.test(hex) ||
    hex.length % 2 !== 0 ||
    (!allowEmpty && hex.length <= 2)
  ) {
    throw new Error(`Invalid bridge order ${field}`);
  }
  return hex.toLowerCase();
}

export function normalizeBridgeOrderExpectation(
  raw: any,
): BridgeOrderExpectation {
  const source =
    raw?.bridge_order_expectation || raw?.bridgeOrderExpectation || raw;
  const takeChainIdRaw = source?.take_chain_id ?? source?.takeChainId;
  const takeChainId = BigInt(takeChainIdRaw || 0);
  if (takeChainId <= 0n) {
    throw new Error("Invalid bridge order takeChainId");
  }
  const takeAmountRaw = source?.take_amount ?? source?.takeAmount;
  const takeAmount = BigInt(takeAmountRaw || 0);
  if (takeAmount <= 0n) {
    throw new Error("Invalid bridge order takeAmount");
  }
  const givePatchAuthoritySrc = String(
    source?.give_patch_authority_src ??
      source?.givePatchAuthoritySrc ??
      ethers.ZeroAddress,
  );
  if (!ethers.isAddress(givePatchAuthoritySrc)) {
    throw new Error("Invalid bridge order givePatchAuthoritySrc");
  }
  return {
    takeChainId,
    takeAmount,
    takeTokenAddress: normalizeBytes(
      source?.take_token_address ?? source?.takeTokenAddress,
      "takeTokenAddress",
    ),
    receiverDst: normalizeBytes(
      source?.receiver_dst ?? source?.receiverDst,
      "receiverDst",
    ),
    givePatchAuthoritySrc: ethers.getAddress(givePatchAuthoritySrc),
    orderAuthorityAddressDst: normalizeBytes(
      source?.order_authority_address_dst ?? source?.orderAuthorityAddressDst,
      "orderAuthorityAddressDst",
    ),
    allowedTakerDst: normalizeBytes(
      source?.allowed_taker_dst ?? source?.allowedTakerDst ?? "0x",
      "allowedTakerDst",
      { allowEmpty: true },
    ),
    allowedCancelBeneficiarySrc: normalizeBytes(
      source?.allowed_cancel_beneficiary_src ??
        source?.allowedCancelBeneficiarySrc ??
        "0x",
      "allowedCancelBeneficiarySrc",
      { allowEmpty: true },
    ),
  };
}

export function computeBridgeOrderExpectationDigest(
  expectation: BridgeOrderExpectation,
): string {
  return ethers.keccak256(
    ABI_CODER.encode(
      [
        "uint256",
        "uint256",
        "bytes",
        "bytes",
        "address",
        "bytes",
        "bytes",
        "bytes",
      ],
      [
        expectation.takeChainId,
        expectation.takeAmount,
        expectation.takeTokenAddress,
        expectation.receiverDst,
        expectation.givePatchAuthoritySrc,
        expectation.orderAuthorityAddressDst,
        expectation.allowedTakerDst,
        expectation.allowedCancelBeneficiarySrc,
      ],
    ),
  );
}

function getMerkleBaseUrl(): string {
  return (
    EVM_CONFIG.PROVER_URL ||
    EVM_CONFIG.PROVER_DIRECT_URL ||
    EVM_CONFIG.NOVA_PROVER_URL ||
    ""
  ).replace(/\/+$/, "");
}

function buildMerkleUrl(base: string, endpoint: string): string {
  return base.includes("?path=")
    ? `${base}${endpoint.replace(/^\//, "")}`
    : `${base}${endpoint}`;
}

export async function findLeafIndexByCommitment(
  commitment: string,
  treeIndex?: number,
  treeKey?: string,
): Promise<number | null> {
  const merkleBase = getMerkleBaseUrl();
  const endpoint = buildMerkleUrl(merkleBase, "/api/v1/public/merkle/find");
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        commitment,
        ...(treeIndex !== undefined ? { tree_index: treeIndex } : {}),
        ...(treeKey ? { tree_pda: treeKey } : {}),
      }),
    });
    if (!response.ok) return null;
    const data = (await response.json()) as {
      found?: boolean;
      leaf_index?: number;
      leafIndex?: number;
    };
    if (!data.found) return null;
    const idx = data.leaf_index ?? data.leafIndex;
    return typeof idx === "number" && idx >= 0 ? idx : null;
  } catch {
    return null;
  }
}

export async function requestMerkleSync(): Promise<void> {
  const merkleBase = getMerkleBaseUrl();
  const endpoint = buildMerkleUrl(merkleBase, "/api/v1/public/merkle/latest");
  try {
    await fetch(endpoint, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  } catch {
    // best-effort freshness probe
  }
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const SHIELDED_POOL_NOVA_ABI = [
  "function joinsplit((bytes payload,(bytes32 merkleRoot,bytes32 nullifierAccumulator,bytes32 commitmentAccumulator,bytes32 statementPublicInput,bytes32 assetBinding,uint64 expiry,bytes32 nonce) receipt) authorization, bytes32 merkleRoot, bytes32[] calldata nullifiers, bytes32[] calldata commitments, bytes32 tokenHash) external",
  "function withdraw((bytes payload,(bytes32 merkleRoot,bytes32 nullifierAccumulator,bytes32 commitmentAccumulator,bytes32 statementPublicInput,bytes32 assetBinding,uint64 expiry,bytes32 nonce) receipt) authorization, bytes32 merkleRoot, bytes32[] calldata nullifiers, address recipient, address token, bytes32 tokenHash, uint256 amount, bytes32 changeCommitment) external",
  "function withdrawWrappedERC721((bytes payload,(bytes32 merkleRoot,bytes32 nullifierAccumulator,bytes32 commitmentAccumulator,bytes32 statementPublicInput,bytes32 assetBinding,uint64 expiry,bytes32 nonce) receipt) authorization, bytes32 merkleRoot, bytes32[] calldata nullifiers, address recipient, bytes32 claimHash, uint256 vaultIdx, address token, uint256 tokenId, uint256 nonce, bytes32 changeCommitment) external",
  "function withdrawWrappedERC1155((bytes payload,(bytes32 merkleRoot,bytes32 nullifierAccumulator,bytes32 commitmentAccumulator,bytes32 statementPublicInput,bytes32 assetBinding,uint64 expiry,bytes32 nonce) receipt) authorization, bytes32 merkleRoot, bytes32[] calldata nullifiers, address recipient, bytes32 claimHash, uint256 vaultIdx, address token, uint256 tokenId, uint256 amount, uint256 nonce, bytes32 changeCommitment) external",
  "function withdrawBatch((bytes payload,(bytes32 merkleRoot,bytes32 nullifierAccumulator,bytes32 commitmentAccumulator,bytes32 statementPublicInput,bytes32 assetBinding,uint64 expiry,bytes32 nonce) receipt) authorization, bytes32 merkleRoot, bytes32[] calldata nullifiers, address token, bytes32 tokenHash, tuple(address recipient,uint256 amount)[] calldata outputs, bytes32[] calldata commitments, uint256 totalAmount, uint256 protocolFee, uint256 partnerFee) external",
  "function deposit(bytes32 commitment, address token, uint256 noteAmount, uint256 protocolFee, uint256 partnerFee, bytes32 tokenHash, bytes proof) external",
  "function knownRoots(bytes32 root) view returns (bool)",
  "function owner() view returns (address)",
  "function transferOwnership(address newOwner) external",
  "function acceptOwnership() external",
];
